import { SLoader } from "@avalara/skylab-sdk/react";
import map from "lodash/map";
import { Suspense } from "react";
import { Navigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { ERROR_PAGE_TYPES } from "../../shared/constants";
import ErrorPage from "../errorHandling/ErrorPage";
import commonRoutesConfig from "./commonRoutesConfig";
import Protected from "./ProtectedRoutes";
import retailRoutesConfig from "./retailRoutesConfig";
import { getGeneratedPathForRoute } from "./utils";

/**
 * Recursively constructs a route configuration by wrapping route elements
 * in a <Protected> component if accessConfig is provided, and assigns a unique ID to each route.
 *
 * @param {Object} routesConfig - The initial route configuration object.
 * Each route object may contain:
 * - `accessConfig`: (optional) Access control settings for the route.
 * - `element`: The React element to render for the route.
 * - `children`: (optional) Nested route configurations.
 *
 * @returns {Array<Object>} The processed route configuration with access protection and unique IDs applied.
 */
const constructRoutes = routesConfig => {
    return map(routesConfig, ({ accessConfig, ...route }) => {
        if (route.children) route.children = constructRoutes(route.children);
        route.element = (
            <Suspense fallback={<SLoader loading="true" aria-live="polite" />}>
                {route.element}
            </Suspense>
        );
        if (accessConfig)
            route.element = <Protected accessConfig={accessConfig}>{route.element}</Protected>;
        return {
            id: uuidv4(),
            ...route,
        };
    });
};

const errorHandlingRoutes = [
    {
        id: uuidv4(),
        path: "*",
        element: <ErrorPage type={ERROR_PAGE_TYPES.PAGE_NOT_FOUND} />,
    },
];

export const allRoutes = [
    ...constructRoutes(commonRoutesConfig),
    ...constructRoutes(retailRoutesConfig),
    ...errorHandlingRoutes,
];

export const retailOnlyRoutes = [
    {
        id: uuidv4(),
        path: "/",
        element: <Navigate to={getGeneratedPathForRoute(retailRoutesConfig?.retail)} />,
    },
    ...constructRoutes(retailRoutesConfig),
    ...errorHandlingRoutes,
];
